// Reading one language's copy of a page, and the rules a translated route
// follows.
//
// Each page keeps one file per language under `$lib/i18n/locales/<locale>/`,
// and its loader hands this module the lazy glob over those files. Lazy, so
// a page in one language downloads that language's text and no other: a
// visitor on /es/pricing never fetches the Japanese copy of the page.

import { error } from "@sveltejs/kit";
import { isTranslatedLocale, localePath, type TranslatedLocale } from "./locales";
import { hasLocale, localesFor } from "./registry";

/** A lazy glob over the per-language files of one page. */
export type LocaleModules = Record<string, () => Promise<unknown>>;

/**
 * The default export of `<locale>/<file>.ts` out of a lazy glob. Throws, naming
 * the file, when the language has none: a missing translation fails the build
 * that prerenders it rather than shipping an empty page.
 */
export async function fromLocale<T>(
  modules: LocaleModules,
  locale: TranslatedLocale,
  file: string,
): Promise<T> {
  const suffix = `/${locale}/${file}.ts`;
  const key = Object.keys(modules).find((path) => path.endsWith(suffix));
  const importer = key === undefined ? undefined : modules[key];
  if (importer === undefined) throw new Error(`No ${file} copy for ${locale}`);
  const module = (await importer()) as { default: T };
  return module.default;
}

/** The prerender entries of a translated route: one per language the page exists in. */
export function localeEntries(path: string): { lang: string }[] {
  return localesFor(path)
    .filter((locale): locale is TranslatedLocale => isTranslatedLocale(locale))
    .map((lang) => ({ lang }));
}

/**
 * The language of a translated route, or a 404 when the page does not exist in
 * it. The parameter matcher has already refused anything that is not a
 * language; this refuses a language the page was not translated into, so
 * `/ja/registro-de-viajeros-hoteles` is not found rather than empty.
 */
export function requireLocale(param: string, path: string): TranslatedLocale {
  if (!isTranslatedLocale(param) || !hasLocale(path, param)) {
    error(404, `Not found: ${localePath(isTranslatedLocale(param) ? param : "en", path)}`);
  }
  return param;
}
